export const paths = {
  ACCOUNT: 'account',
  ASSET_SETTLEMENT: 'asset-settlement',
  BASE: '/',
  DASHBOARD: 'dashboard',
  DATA_MANAGEMENT: 'data-management',
  DOMESTIC_DASHBOARD: 'domestic-dashboard',
  EVENTS_SUMMARY_UI: 'events-summary',
  FLEX_INNOVATIONS: 'flex-innovations',
  FLEX_TRADE_MANAGER: 'flex-trade-manager',
  PORTFOLIO_OPTIMIZER: 'portfolio-optimizer',
  GITBOOK: 'help',
  JAPAN_MARKETS: 'markets',
  LOGIN: 'login',
  LOGOUT: 'logout',
  MODBUS_MAP: 'modbus-map',
  ONBOARDING: 'onboarding',
  REDIRECT: 'redirect',
  REPORTING: 'reports',
  SCHEDULES: 'schedules',
  USER_MANAGEMENT: 'user-management',
  VERIFY_MANAGED_SUBSCRIPTION: 'verify-managed-subscription',
} as const;

export const frontEnds = {
  cashflow: {
    moduleResource: '/cashflow',
    serviceUrl: `/${paths.ASSET_SETTLEMENT}`,
    productGroup: 'infraflex',
  },
  changePassword: {
    serviceUrl: `/${paths.ACCOUNT}`,
    productGroup: 'user-management',
  },
  dataManagement: {
    moduleResource: '/static-data-management',
    serviceUrl: `/${paths.DATA_MANAGEMENT}`,
    productGroup: 'data-management',
  },
  domesticDashboard: {
    moduleResource: '/domestic/core',
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}`,
    productGroup: 'smartflex',
  },
  chargeAssessment: {
    moduleResource: '/domestic/core',
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}/charge-assessment`,
    productGroup: 'smartflex',
  },
  deviceDatabase: {
    moduleResource: '/domestic/core',
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}/device-database`,
    productGroup: 'smartflex',
  },
  deviceOverview: {
    moduleResource: '/domestic/core',
    // This needs to service two routes so we need to use the base path
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}`,
    productGroup: 'smartflex',
  },
  fleetControls: {
    moduleResource: '/domestic/core',
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}/fleet-controls`,
    productGroup: 'smartflex',
  },
  residentialPortfolio: {
    moduleResource: '/domestic/core',
    // This needs to service the portfolio and the base path
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}`,
    productGroup: 'smartflex',
  },
  signalSubmission: {
    moduleResource: '/domestic/core',
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}/signal-submission`,
    productGroup: 'smartflex',
  },
  siteOverview: {
    moduleResource: '/domestic/core',
    // This needs to service multiple routes so we need to use the base path
    serviceUrl: `/${paths.DOMESTIC_DASHBOARD}`,
    productGroup: 'smartflex',
  },
  eventsSummary: {
    moduleResource: '/events',
    serviceUrl: `/${paths.EVENTS_SUMMARY_UI}`,
    productGroup: 'infraflex',
  },
  flexConsole: {
    moduleResource: '/dispatch-and-control/core',
    serviceUrl: `/${paths.DASHBOARD}`,
    productGroup: 'infraflex',
  },
  flexTradeManager: {
    moduleResource: '/trade-manager/flex-ui',
    serviceUrl: `/${paths.FLEX_TRADE_MANAGER}`,
    productGroup: 'infraflex',
  },
  gitbook: {
    serviceUrl: `/${paths.GITBOOK}`,
  },
  japanMarkets: {
    moduleResource: '/japan-markets',
    serviceUrl: `/${paths.JAPAN_MARKETS}`,
    productGroup: 'infraflex',
  },
  modbusMap: {
    // NOTE: this module doesn't exist yet, naming TBD
    moduleResource: '/modbus-map',
    serviceUrl: `/${paths.MODBUS_MAP}`,
    productGroup: 'infraflex',
  },
  login: {
    serviceUrl: `/${paths.LOGIN}`,
  },
  logout: {
    serviceUrl: `/${paths.LOGOUT}`,
  },
  onboarding: {
    moduleResource: '/onboarding',
    serviceUrl: `/${paths.ONBOARDING}`,
    productGroup: 'infraflex',
  },
  reports: {
    moduleResource: '/reporting',
    serviceUrl: `/${paths.REPORTING}`,
    productGroup: 'infraflex',
  },
  schedules: {
    moduleResource: '/schedules',
    serviceUrl: `/${paths.SCHEDULES}`,
    productGroup: 'infraflex',
  },
  userManagement: {
    moduleResource: '/user-management',
    serviceUrl: `/${paths.USER_MANAGEMENT}`,
    productGroup: 'user-management',
  },
  portfolioOptimizer: {
    moduleResource: '/trade-manager/flex-ui',
    serviceUrl: `/${paths.PORTFOLIO_OPTIMIZER}`,
    productGroup: 'infraflex',
  },
} as const;

/**
 * single-spa doesn't seem to provide a nice way of getting the base path as defined in the route template,
 * hence these duplicated paths which need to be passed down to some of the apps in order for their routing to
 * work.
 */
export const basePaths: Record<string, string> = {
  '@upside-spa/cashflow': frontEnds.cashflow.serviceUrl,
  '@upside-spa/data-management-ui': frontEnds.dataManagement.serviceUrl,
  '@upside-spa/charge-assessment-ui': frontEnds.chargeAssessment.serviceUrl,
  '@upside-spa/device-database-ui': frontEnds.deviceDatabase.serviceUrl,
  '@upside-spa/device-overview-ui': frontEnds.deviceOverview.serviceUrl,
  '@upside-spa/fleet-controls-ui': frontEnds.fleetControls.serviceUrl,
  '@upside-spa/portfolio-ui': frontEnds.residentialPortfolio.serviceUrl,
  '@upside-spa/signal-submission-ui': frontEnds.signalSubmission.serviceUrl,
  '@upside-spa/site-overview-ui': frontEnds.siteOverview.serviceUrl,
  '@upside-spa/flex-console-ui': frontEnds.flexConsole.serviceUrl,
  '@upside-spa/gitbook-ui': frontEnds.gitbook.serviceUrl,
  '@upside-spa/japan-markets-viewer': frontEnds.japanMarkets.serviceUrl,
  '@upside-spa/modbus-map-ui': frontEnds.modbusMap.serviceUrl,
  '@upside-spa/asset-onboarding-ui': frontEnds.onboarding.serviceUrl,
  '@upside-spa/reports-ui': frontEnds.reports.serviceUrl,
  '@upside-spa/schedules-ui-v2': frontEnds.schedules.serviceUrl,
  '@upside-spa/user-management-ui': frontEnds.userManagement.serviceUrl,
  '@upside-spa/trade-manager': frontEnds.portfolioOptimizer.serviceUrl,
};

export const appHeaderAppName = '@upside-spa/app-header';
export const globalHeaderAppName = '@upside-spa/global-header';
export const navigationAppName = '@upside-spa/navigation';
export const infraflexAiAssistantAppName = '@upside-spa/infraflex-assistant-ui';
export const devToolsAppName = '@upside-spa/dev-tools';
export const amiEventsUiAppName = '@upside-spa/ami-events-ui';
